import * as z from "zod";
import { sha256HexText, type ValidationIssue } from "./index";
import { type SyncFailure, type SyncStatus, syncFailures, syncStatuses, type TilSystem, tilSystems } from "./til-enums";
import { instant } from "./til-fields";

// What every domain that receives a message of a system reads the same way: the item of an inbound row and the SKU
// it resolves to (I-11, I-12), whether a whole sent in parts has arrived (I-12, I-28), and how a fact that waits is
// listed and applied again (I-11 §5); and the most units one line of a message may carry, which the inputs of the
// domains share so that what Nexxt accepts it can also send (ADR-144).
export {
  type FoundItems,
  itemKey,
  MAX_LINE_UNITS,
  partsComplete,
  type SystemItem,
  skuOfItem,
} from "./til-fields";
export * from "./til-waiting";

// Portal ↔ TIL contract, common part (ADR-049, ADR-058, ADR-061, ADR-090, ADR-096, ADR-100,
// docs/40-integrazioni/contratto-til.md): the envelope of every message in both directions, the synchronous answer,
// the asynchronous outcome and the route a file named by a message is downloaded from; errors are the
// `ApiErrorBody` of every Nexxt API. Nexxt owns it; it changes only in compatible ways (ADR-020) and every receiver
// ignores the fields it does not know.

/** Version of the contract documents (semver): minor for compatible additions, 1.0.0 once TIL has reviewed it. */
export const TIL_CONTRACT_VERSION = "0.27.0";

// The systems, the outcome suffix and the sync states are tuples without zod (ADR-271): @nexxt/db builds its enums from
// them through `@nexxt/contracts/til/enums`.
export {
  externalSystems,
  type SyncFailure,
  type SyncStatus,
  syncFailures,
  syncStatuses,
  TIL_OUTCOME_MINUTES,
  TIL_OUTCOME_SUFFIX,
  type TilSystem,
} from "./til-enums";

/** The routes of both APIs: one per message type, and one for the outcomes (ADR-049). */
export const tilMessagePath = (type: string) => `/v1/messages/${type}`;
export const TIL_OUTCOMES_PATH = "/v1/outcomes";

/** Largest request body in both directions (1 MiB): a bigger message answers 413 and its I-xx splits it. */
export const TIL_MAX_BODY_BYTES = 1_048_576;

/**
 * A message sent again on purpose with the same key (the relaunch of a document, I-04 §4): the outbox has a row for
 * each sending, unique by its key, so the n-th has `<key>#<n>`; the message carries `<key>` and its `shortKey`, so
 * TIL and the system see one key (ADR-118).
 */
export const resendKey = (key: string, sending: number) => (sending === 1 ? key : `${key}#${sending}`);
/** The key a row of the outbox gives its message: `resendKey` undone. */
export const messageKeyOf = (rowKey: string) => rowKey.replace(/#\d+$/, "");

/** Route of the TIL API a file named by a message is downloaded from: files never travel in an envelope (ADR-096). */
export const tilFilePath = (ref: string) => `/v1/files/${ref}`;

/** Largest file a message can name (20 MiB). */
export const TIL_MAX_FILE_BYTES = 20 * 1_048_576;

/** How a message names a file: safe in a path as it is, and short enough for an idempotency key. */
export const tilFileRef = z.string().regex(/^[A-Za-z0-9._~-]{1,80}$/);

/** A PDF a message names (I-07, I-33): what Nexxt downloads from `tilFilePath`, and checks the download against. */
export const tilPdfFile = z.object({
  ref: tilFileRef.describe("Reference of the file on the TIL API, new for each file: `GET /v1/files/<ref>` returns it"),
  fileName: z.string().min(1).max(200).describe("Name of the file"),
  contentType: z.literal("application/pdf"),
  sizeBytes: z.number().int().min(1).max(TIL_MAX_FILE_BYTES),
  sha256: sha256HexText.describe("SHA-256 of the file, lowercase hex: Nexxt refuses a download that does not match"),
});
export type TilPdfFile = z.output<typeof tilPdfFile>;

/**
 * Largest payload of a part as a domain writes it in the outbox, in bytes of JSON: the message integration sends
 * must stay under `TIL_MAX_BODY_BYTES` once it has the envelope and the ids of the system, which the domain does not
 * see.
 */
export const TIL_PART_BYTES = 700_000;

const messageId = z
  .uuid()
  .describe(
    "Id of the message, the same on every retry: the receiver deduplicates on it. Outbound: the `platform.outbox` id",
  );

const idempotencyKey = z
  .string()
  .min(1)
  .max(120)
  .describe(
    "Business key of the fact, built as the I-xx says (e.g. `I-04:<fulfillmentId>:invoice`): the receiver, and the " +
      "system behind it, never apply twice the effect of one key. A resync sends a new key on purpose",
  );

/** Longest key the shortest field of a system holds: the External Document No. of BC (ADR-058). */
export const TIL_SHORT_KEY_MAX = 20;

/** A short key as it is written, in an envelope or wherever a message quotes the one of another message. */
export const shortKeyFormat = z.string().regex(new RegExp(`^[0-9A-Z]{1,${TIL_SHORT_KEY_MAX}}$`));

const shortKey = shortKeyFormat.describe(
  `\`idempotencyKey\` in at most ${TIL_SHORT_KEY_MAX} characters, computed by Nexxt from it (ADR-058): TIL writes ` +
    "it in the system's own reference field (e.g. the External Document No. of BC), so the same fact is never " +
    "applied twice. Present when the I-xx asks for it",
);

const company = z
  .string()
  .min(1)
  .max(20)
  .describe("Legal entity code (`reference.legal_entity.code`, e.g. `NEXXT_IT`) when the fact belongs to one");

const correlationId = z
  .string()
  .min(1)
  .max(100)
  .describe("Id of the request or process that caused the message (`x-request-id`): quote it when reporting a problem");

/** The fields of every message; the others come from its definition. */
const envelopeFields = {
  messageId,
  idempotencyKey,
  occurredAt: instant.describe(
    "When the fact happened: delivery order is not guaranteed, and the I-xx says how the receiver uses this to " +
      "discard an older state",
  ),
  correlationId,
};

const brandId = z.uuid().optional().describe("Nexxt id of the brand when the fact belongs to one");

const MESSAGE_TYPE_RE = /^I-\d{2}$/;
type TilMessageType = `I-${string}`;

/** Nexxt → TIL, TIL → Nexxt, or both (I-00). `outbound` and `inbound` as in `platform.message_direction`. */
type TilDirection = "outbound" | "inbound" | "both";

/**
 * The two classes of the outbound channel (ADR-090). `transactional`: a fact a person or a process is waiting for (an
 * order, an ASN), small; it leaves first. `bulk`: the state of many rows at once (an item master), which can wait and
 * never holds a transactional message back.
 */
type TilPriority = "transactional" | "bulk";

interface TilMessageDefinition<P extends z.ZodType> {
  type: TilMessageType;
  /** Payload version: a breaking change is a new version, accepted next to the old one (ADR-049). */
  version: number;
  direction: TilDirection;
  /** Where the message goes (outbound) or where it comes from (inbound). */
  system: TilSystem;
  /** The other systems an inbound message may come from, besides `system`: the envelope names which one (ADR-144). */
  alsoFrom?: readonly TilSystem[];
  summary: string;
  description: string;
  /** How `idempotencyKey` is built. */
  idempotencyKey: string;
  /** `required` for the systems whose reference field is shorter than `idempotencyKey` (BC). */
  shortKey?: "required";
  /** `required` when the fact always belongs to a legal entity, and the system needs it to pick its company. */
  company?: "required";
  /** Class of the outbound channel; `transactional` when left out. It says nothing of an inbound message. */
  priority?: TilPriority;
  /**
   * The Nexxt aggregate whose messages to one system leave one at a time, in the order of their facts (ADR-090): the
   * next one waits for the outcome of the one before. Left out, the messages of the type do not depend on each other.
   */
  orderedBy?: string;
  /** Minutes the outcome may take when the system needs more than `TIL_OUTCOME_MINUTES`; then it is overdue (M16). */
  outcomeMinutes?: number;
  /**
   * Nexxt entities the system assigns an id to, each with the ids a payload carries: a `processed` outcome has an
   * `externalRefs` entry for each one sent, and one without them is an error, not a success (spec v0.2 §4 rule 3,
   * ADR-061).
   */
  expectedRefs?: Record<string, (payload: z.output<P>) => string[]>;
  payload: P;
  /** Full example message, validated by the tests and published in the documents. */
  example: {
    messageId: string;
    idempotencyKey: string;
    shortKey?: string;
    occurredAt: string;
    correlationId: string;
    company?: string;
    brandId?: string;
    payload: z.input<P>;
  };
}

/** A Nexxt entity as an outcome names it in `externalRefs`. */
export interface TilEntityRef {
  entityType: string;
  entityId: string;
}

/** A message of the contract: its envelope schema is a named component of the OpenAPI documents. */
export function defineTilMessage<P extends z.ZodType>({ expectedRefs = {}, ...def }: TilMessageDefinition<P>) {
  if (!MESSAGE_TYPE_RE.test(def.type)) throw new Error(`TIL message type must be I-nn: ${def.type}`);
  const envelope = z
    .object({
      ...envelopeFields,
      company: def.company === "required" ? company : company.optional(),
      brandId,
      shortKey: def.shortKey === "required" ? shortKey : shortKey.optional(),
      type: z.literal(def.type),
      // A literal alone is published as `number`: the documents say `integer`, as in the outcome
      version: z.literal(def.version).meta({ type: "integer", enum: [def.version] }),
      system: def.alsoFrom ? z.enum([def.system, ...def.alsoFrom]) : z.literal(def.system),
      payload: def.payload,
    })
    .meta({ id: `${def.type}.v${def.version}`, description: `${def.summary}. ${def.description}` });
  const example = { type: def.type, version: def.version, system: def.system, ...def.example };
  return {
    ...def,
    priority: def.priority ?? "transactional",
    envelope,
    example,
    expectedRefTypes: Object.keys(expectedRefs),
    /**
     * The entities of a payload the system assigns an id to: what a `processed` outcome must answer for. It reads
     * only Nexxt ids, which the payload a domain leaves in the outbox carries in the same fields as the message
     * (ADR-064): what a sent row is owed is known without translating it again.
     */
    expectedRefsOf(payload: unknown): TilEntityRef[] {
      return Object.entries(expectedRefs).flatMap(([entityType, idsOf]) =>
        idsOf(payload as z.output<P>).map((entityId) => ({ entityType, entityId })),
      );
    },
  };
}
export type TilMessage = ReturnType<typeof defineTilMessage>;

/** Whether a message travels from Nexxt to TIL (`outbound`) or from TIL to Nexxt (`inbound`). */
export const travels = (message: TilMessage, direction: "outbound" | "inbound") =>
  message.direction === direction || message.direction === "both";

/** What every envelope has, whatever its type: enough to store, log and answer a message. */
export interface TilEnvelope {
  messageId: string;
  type: string;
  version: number;
  system: TilSystem;
  idempotencyKey: string;
  shortKey?: string | undefined;
  occurredAt: string;
  correlationId: string;
  company?: string | undefined;
  brandId?: string | undefined;
  payload: unknown;
}

// --- Synchronous answer ---

/** 202 of every POST: the receiver stored the message (or the outcome) and will process it. */
export const tilAccepted = z
  .object({
    id: z.uuid().describe("`messageId` or `outcomeId` of the request"),
    status: z
      .enum(["accepted", "duplicate"])
      .describe("`duplicate`: already received with the same content, nothing new happens"),
    receivedAt: instant,
  })
  .meta({ id: "TilAccepted" });

// --- Asynchronous outcome ---

/**
 * `processed`: accepted by the system, which may still have to carry it out (GXO ships later, and says so with I-11);
 * `rejected`: refused by it for business reasons, in full or in part (ADR-061); `failed`: technical, given up.
 */
const tilOutcomeStatuses = ["processed", "rejected", "failed"] as const;

/**
 * Rows of an outcome, ids and errors alike: as many as the largest message has rows, so every row error reaches the
 * user (spec v0.2 §4 rule 3, ADR-061).
 */
const TIL_MAX_OUTCOME_ROWS = 10_000;

/**
 * How a message ended at the system behind the receiver. TIL sends one for each outbound message; Nexxt sends one only
 * for the inbound messages whose I-xx asks for it (and for I-00).
 */
export const tilOutcome = z
  .object({
    outcomeId: z.uuid().describe("Id of the outcome, the same on every retry: the receiver deduplicates on it"),
    messageId: z.uuid().describe("The message this outcome is about"),
    type: z.string().regex(MESSAGE_TYPE_RE),
    version: z.number().int().min(1),
    idempotencyKey,
    system: z.enum(tilSystems).describe("The system that processed the message"),
    status: z
      .enum(tilOutcomeStatuses)
      .describe(
        "`processed`: the system accepted the whole message, not that it has physically carried it out; `rejected`: " +
          "refused for business reasons, in full or in part: `errors` says which rows, `externalRefs` which ones " +
          "the system did accept; `failed`: technical error, retries given up",
      ),
    processedAt: instant,
    correlationId,
    externalRefs: z
      .array(
        z.object({
          entityType: z.string().min(1).max(80).describe("Nexxt entity, e.g. `catalog.sku`"),
          entityId: z.uuid().describe("Nexxt id, as sent in the message"),
          externalId: z.string().min(1).max(200).describe("Id or number in the system, e.g. the BC item no."),
        }),
      )
      .max(TIL_MAX_OUTCOME_ROWS)
      .optional()
      .describe(
        "Ids the system assigned: Nexxt stores them in `platform.external_ref`. With `processed`, one for each " +
          "entity the I-xx expects an id for: none at all is an error. With `rejected`, the rows the system accepted",
      ),
    errors: z
      .array(
        z.object({
          code: z
            .string()
            .min(1)
            .max(60)
            .describe("Stable code, e.g. `item_blocked`; the system's own in `externalCode`"),
          message: z.string().max(1000),
          path: z
            .string()
            .max(200)
            .optional()
            .describe("Row and field of the payload, e.g. `payload.products.3.skus.0.size`"),
          externalCode: z.string().max(100).optional(),
        }),
      )
      .max(TIL_MAX_OUTCOME_ROWS)
      .optional()
      .describe(
        "Why, for `rejected` and `failed`: one for each row and field refused, none left out, shown to the user " +
          "who started the action and in the M16 error view",
      ),
  })
  .meta({ id: "TilOutcome" });
export type TilOutcome = z.output<typeof tilOutcome>;
/** The row errors of an outcome: what a domain keeps on the entity it sent, with the row each one is about. */
export type TilOutcomeErrors = NonNullable<TilOutcome["errors"]>;
/** The ids a system answered in an outcome, each for the Nexxt entity it names. */
export type TilExternalRefs = NonNullable<TilOutcome["externalRefs"]>;

/**
 * How a message stands for the domain that wrote it: still `sent`, answered by the system (the statuses of an
 * outcome), or given up by Nexxt before TIL took it (`dead`).
 */
export const tilPartStatuses = ["sent", ...tilOutcomeStatuses, "dead"] as const;
export type TilPartStatus = (typeof tilPartStatuses)[number];

const ROW_PATH = /^payload\.([^.]+)\.(\d+)(?:\.(.+))?$/;

/**
 * Where the `path` of an outcome error points: `payload.lines.12.externalId` → row 12 of `lines` and the field in
 * it. `rows` names the arrays of the payload a domain keeps its rows by; an error of the whole message, or outside
 * them, has no row and keeps its path as the field.
 */
export function outcomeErrorRow(
  path: string | undefined,
  rows: readonly string[],
): { index: number | null; field: string | null } {
  const [, root, index, field] = ROW_PATH.exec(path ?? "") ?? [];
  if (root === undefined || index === undefined || !rows.includes(root)) return { index: null, field: path ?? null };
  return { index: Number(index), field: field ?? null };
}

// --- The state of an entity a domain sends whole to a system (ADR-065, ADR-093, ADR-106): how the entity stands, why
// its last state is not there, and the errors it shows. One wording for every domain, so the apps have one badge
// and one text for each. ---

/** How a state sent ends: on the system, or not. */
export type SyncEnd = Extract<SyncStatus, "sync_complete" | "sync_failed">;

const worstFirst: [TilPartStatus, SyncFailure][] = [
  ["dead", "message_dead"],
  ["failed", "failed"],
  ["rejected", "rejected"],
];

/** Why a state whose messages all ended is not on the system: the worst way one of them ended; null if it is. */
export const syncFailureOf = (statuses: readonly TilPartStatus[]): SyncFailure | null =>
  worstFirst.find(([status]) => statuses.includes(status))?.[1] ?? null;

/**
 * Whether a message that stands as `sent` (or ended as `status`) still waits for the outcome `fact`: the first outcome
 * settles it, a second delivery of any outcome does not. A message Nexxt gave up on is settled again by the outcome it
 * gets when someone sends it once more from the error queue (M16).
 */
export const outcomeAwaited = (status: TilPartStatus | null, fact: Exclude<TilPartStatus, "sent">): boolean =>
  status === "sent" || (status === "dead" && fact !== "dead");

/** What the system refused, or what Nexxt lacks to send the state: one per field. */
export const syncErrorDto = z.object({
  /** Field of the message (`externalId`), or the reference value to map (`business_central.location`). */
  field: z.string().nullable(),
  code: z.string(),
  message: z.string(),
  /** The code the system gives the error, when it has one. */
  externalCode: z.string().nullable(),
});
export type SyncError = z.output<typeof syncErrorDto>;

/**
 * The same error on an aggregate sent with its lines (a PO, a sales order: ADR-093, ADR-120): `lineNo` is the number of
 * the line it is about, null when it is about the whole aggregate.
 */
export const lineSyncErrorDto = z.object({ lineNo: z.number().int().nullable(), ...syncErrorDto.shape });
export type LineSyncError = z.output<typeof lineSyncErrorDto>;

/**
 * How an aggregate with lines stands towards a system (spec v0.2 §4 rule 9; `syncView` of @nexxt/core/db): a PO, a
 * sales order, a shipment, an inbound. `not_synced` until the system must know it; `revision` counts the states sent,
 * 0 before the first; `externalId` is the id the system gave it, from its first `processed` outcome; `parts` are the
 * messages of the last state sent, one unless it is over 1 MiB; `errors` what the system refused or what Nexxt lacks
 * to send it, one per line and field; `canResync` whether it can be sent again now.
 */
export const aggregateSyncDto = z.object({
  status: z.enum(syncStatuses),
  revision: z.number().int(),
  externalId: z.string().nullable(),
  requestedAt: z.iso.datetime().nullable(),
  failure: z.enum(syncFailures).nullable(),
  parts: z.array(z.object({ index: z.number().int(), status: z.enum(tilPartStatuses) })),
  errors: z.array(lineSyncErrorDto),
  canResync: z.boolean(),
});

/** What Nexxt lacks to send a state, as an error the entity shows: the issue a request to send would refuse with. */
export const issueError = (issue: ValidationIssue): SyncError => ({
  field: issue.field,
  code: issue.rule,
  message: issue.value ?? "",
  externalCode: null,
});

/** An error of an outcome as the entity shows it, on the field the domain found its `path` to point at. */
export const outcomeError = (error: TilOutcomeErrors[number], field: string | null): SyncError => ({
  field,
  code: error.code,
  message: error.message,
  externalCode: error.externalCode ?? null,
});

/** `payload.addresses.2.email` of an outcome → the field of the message, as an issue of Nexxt names it. */
export const messageFieldOf = (path: string | undefined): string | null => path?.replace(/^payload\./, "") ?? null;

// --- Messages. I-00 here; the I-xx of each domain in its own file (til-<domain>.ts), all listed in til-messages.ts. ---

/** I-00: checks the channel end to end in both directions, with no effect on any system. */
export const i00Ping = defineTilMessage({
  type: "I-00",
  version: 1,
  direction: "both",
  system: "til",
  summary: "Ping",
  description:
    "Checks the channel end to end with no business effect: the receiver answers 202 and then sends the outcome " +
    "`processed` with no `externalRefs`. Used by the smoke tests of each environment and by TIL during development.",
  idempotencyKey: "`I-00:<messageId>`",
  payload: z.object({
    note: z.string().max(200).optional().describe("Free text, e.g. who is testing"),
  }),
  example: {
    messageId: "0199a3c2-5e7b-7c11-9d2e-3f4a5b6c7d8e",
    idempotencyKey: "I-00:0199a3c2-5e7b-7c11-9d2e-3f4a5b6c7d8e",
    occurredAt: "2026-10-01T08:30:00.000Z",
    correlationId: "smoke-preview-20261001-0830",
    payload: { note: "smoke test" },
  },
});

/** Example outcome of I-00, published with the outcome routes. */
export const tilOutcomeExample: z.input<typeof tilOutcome> = {
  outcomeId: "0199a3c2-6a01-7f3e-8b44-1c2d3e4f5a6b",
  messageId: i00Ping.example.messageId,
  type: "I-00",
  version: 1,
  idempotencyKey: i00Ping.example.idempotencyKey,
  system: "til",
  status: "processed",
  processedAt: "2026-10-01T08:30:01.250Z",
  correlationId: i00Ping.example.correlationId,
};
